import React from "react";
import { styled } from "styled-components";
import { tokens } from "./tokens";

/**
 * Central pill button for the Tractor State feature — filter chips, sort chips,
 * and the detail ticket-sort all render through this so they share one size,
 * one interaction, and one focus ring. `tone` sets the active fill: "ink" for
 * the primary filter chips, "blue" for sort selections.
 */
interface ChipProps {
  active?: boolean;
  tone?: "ink" | "blue";
  onClick?: () => void;
  title?: string;
  children: React.ReactNode;
}

const Chip: React.FC<ChipProps> = ({
  active = false,
  tone = "ink",
  onClick,
  title,
  children,
}) => (
  <Button
    type="button"
    // A toggle, so say which way it is set — the fill alone told a screen
    // reader nothing.
    aria-pressed={active}
    $active={active}
    $tone={tone}
    onClick={onClick}
    title={title}
  >
    {children}
  </Button>
);

const activeColor = (tone: "ink" | "blue") =>
  tone === "blue" ? tokens.blue : tokens.ink;

const Button = styled.button<{ $active: boolean; $tone: "ink" | "blue" }>`
  padding: 7px 16px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  transition:
    background-color 0.12s,
    color 0.12s,
    border-color 0.12s;
  border: 1px solid ${(p) => (p.$active ? activeColor(p.$tone) : tokens.line)};
  background: ${(p) => (p.$active ? activeColor(p.$tone) : tokens.white)};
  color: ${(p) => (p.$active ? tokens.white : tokens.ink)};

  &:hover {
    border-color: ${(p) => (p.$active ? activeColor(p.$tone) : tokens.slate)};
  }

  /* Kill the UA's rectangular outline; show a radius-matching ring (outline
     follows the pill's rounded corners in modern browsers) for keyboard focus. */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

export default Chip;
